import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { FiCalendar, FiCheckCircle, FiClock, FiXCircle, FiTrash2, FiSearch, FiRefreshCw, FiMail, FiPhone, FiCpu, FiExternalLink } from 'react-icons/fi';
import styles from './DemoRequests.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const DemoRequests = () => {
  const [demos, setDemos] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchDemos = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const url = new URL(`${API_BASE_URL}/api/demo/admin`);
      if (filterStatus !== 'all') url.searchParams.append('status', filterStatus);
      if (searchQuery) url.searchParams.append('search', searchQuery);

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch demo requests');
      }

      setDemos(data.demos || []);
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error fetching demo requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchQuery]);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  const handleUpdateStatus = async (id, newStatus) => {
    setActionLoading(id);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_BASE_URL}/api/demo/admin/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update status');
      }

      toast.success(`Demo request marked as ${newStatus}`);
      fetchDemos();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this demo request?')) return;

    setActionLoading(id);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_BASE_URL}/api/demo/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete demo request');
      }

      toast.success('Demo request deleted successfully');
      fetchDemos();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to delete request');
    } finally {
      setActionLoading(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'DS';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.headerRow}>
        <div className={styles.titleGroup}>
          <h1>SaleSense AI Demo Requests</h1>
          <div className={styles.subtitle}>
            Manage incoming sales platform demos, track interest, and confirm client time slots.
          </div>
        </div>
        <button onClick={fetchDemos} className={styles.refreshBtn} disabled={loading}>
          <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh Data
        </button>
      </div>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <div className={styles.statLabel}>Total Requests</div>
            <div className={styles.statNumber}>{stats.total}</div>
          </div>
          <div className={styles.statIcon} style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8' }}>
            <FiCalendar size={24} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <div className={styles.statLabel}>Pending Review</div>
            <div className={styles.statNumber}>{stats.pending}</div>
          </div>
          <div className={styles.statIcon} style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24' }}>
            <FiClock size={24} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <div className={styles.statLabel}>Accepted Demos</div>
            <div className={styles.statNumber}>{stats.accepted}</div>
          </div>
          <div className={styles.statIcon} style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#34d399' }}>
            <FiCheckCircle size={24} />
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statInfo}>
            <div className={styles.statLabel}>Rejected</div>
            <div className={styles.statNumber}>{stats.rejected}</div>
          </div>
          <div className={styles.statIcon} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171' }}>
            <FiXCircle size={24} />
          </div>
        </div>
      </div>

      {/* Toolbar & Filters */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch color="#64748b" size={18} />
          <input
            type="text"
            placeholder="Search by prospect name, email, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterTabs}>
          {['all', 'pending', 'accepted', 'rejected'].map((st) => (
            <button
              key={st}
              onClick={() => setFilterStatus(st)}
              className={`${styles.filterBtn} ${filterStatus === st ? styles.filterBtnActive : ''}`}
            >
              {st.charAt(0).toUpperCase() + st.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Requests Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.emptyState}>Loading demo requests...</div>
        ) : demos.length === 0 ? (
          <div className={styles.emptyState}>No demo requests found matching your filter criteria.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Prospect Info</th>
                <th>Solution Interest</th>
                <th>Requested Date & Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {demos.map((demo) => (
                <tr key={demo._id}>
                  <td>
                    <div className={styles.userCell}>
                      <div className={styles.avatarCircle}>{getInitials(demo.name)}</div>
                      <div>
                        <div className={styles.userName}>{demo.name}</div>
                        <div className={styles.userMeta}>
                          <a href={`mailto:${demo.email}`} style={{ color: '#38bdf8', textDecoration: 'none' }}>
                            <FiMail size={12} style={{ display: 'inline', marginRight: 4 }} />
                            {demo.email}
                          </a>
                          <span style={{ margin: '0 6px' }}>•</span>
                          <a href={`tel:${demo.phone}`} style={{ color: '#94a3b8', textDecoration: 'none' }}>
                            <FiPhone size={12} style={{ display: 'inline', marginRight: 4 }} />
                            {demo.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.solutionTag}>
                      <FiCpu size={12} /> {demo.solutionInterest || 'SaleSense AI'}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#ffffff' }}>
                      {new Date(demo.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: 600 }}>
                      {new Date(demo.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td>
                    {demo.status === 'accepted' ? (
                      <div className={styles.badgeAccepted}>
                        <FiCheckCircle size={14} /> Accepted
                      </div>
                    ) : demo.status === 'rejected' ? (
                      <div className={styles.badgeRejected}>
                        <FiXCircle size={14} /> Rejected
                      </div>
                    ) : (
                      <div className={styles.badgePending}>
                        <FiClock size={14} /> Pending Review
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={styles.actionCell}>
                      {demo.bookingUrl && (
                        <a
                          href={demo.bookingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.btnAccept}
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          Meeting <FiExternalLink size={12} />
                        </a>
                      )}
                      {demo.status !== 'accepted' && (
                        <button
                          className={styles.btnAccept}
                          disabled={actionLoading === demo._id}
                          onClick={() => handleUpdateStatus(demo._id, 'accepted')}
                        >
                          Accept
                        </button>
                      )}
                      {demo.status !== 'rejected' && (
                        <button
                          className={styles.btnReject}
                          disabled={actionLoading === demo._id}
                          onClick={() => handleUpdateStatus(demo._id, 'rejected')}
                        >
                          Reject
                        </button>
                      )}
                      <button
                        className={styles.btnDelete}
                        disabled={actionLoading === demo._id}
                        onClick={() => handleDelete(demo._id)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DemoRequests;
